import { useState, memo } from "react";
import { ChipGroup, EmptyState, useBreakpoint } from "../../ui";
import { fmtMoney, fmtMoneyShort } from "./money";

/**
 * RevenueChart — line chart of Invoiced / Received / Expenses for the selected period, with a
 * toggle chip per series (Expenses dashed). Lines are SVG; the axis labels, hover dots and the
 * tooltip are HTML, so text keeps its real size however wide the chart is drawn.
 *
 * Props:
 *   data — array of { label, invoiced, received, expenses, net }
 */

export const SERIES = [
  { key: "invoiced", label: "Invoiced" },
  { key: "received", label: "Received" },
  { key: "expenses", label: "Expenses" },
];

// The drawing box (the SVG is stretched to the card's width; strokes do not scale).
const W = 600;
const H = 220;
const PAD_Y = 8;

// A round top for the y axis: three equal steps of 1, 2, 2.5 or 5 x 10^n.
function niceTop(max) {
  if (max <= 0) return 300;
  const raw = max / 3;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map(f => f * mag).find(s => s >= raw);
  return step * 3;
}

function RevenueChart({ data, title = "Revenue trend" }) {
  const [shown, setShown] = useState(SERIES.map(s => s.key));
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const phone = useBreakpoint() === "phone";

  if (!data || data.length === 0) {
    return (
      <div className="fin-chart">
        <h2 className="fin-h2">{title}</h2>
        <EmptyState title="No figures for this period">Pick another period to see how money came in.</EmptyState>
      </div>
    );
  }

  const n = data.length;
  const top = niceTop(Math.max(...data.flatMap(d => SERIES.map(s => d[s.key] || 0))));
  const xPct = i => (n === 1 ? 50 : (i / (n - 1)) * 100);
  const yOf = val => PAD_Y + (1 - Math.max(0, val) / top) * (H - PAD_Y * 2);
  const yPct = val => (yOf(val) / H) * 100;
  const points = key => data.map((d, i) => `${((xPct(i) / 100) * W).toFixed(1)},${yOf(d[key] || 0).toFixed(1)}`).join(" ");

  const ticks = [top, (top * 2) / 3, top / 3, 0];

  // Thin the x labels so they never collide: at most 3 on a phone, 8 elsewhere; first and last kept.
  const maxLabels = phone ? 3 : 8;
  const every = Math.max(1, Math.ceil(n / maxLabels));
  const showLabel = i => i === 0 || i === n - 1 || (i % every === 0 && n - 1 - i >= every);

  const visible = SERIES.filter(s => shown.includes(s.key));
  const tip = hoveredIdx != null ? data[hoveredIdx] : null;

  function pick(e) {
    const r = e.currentTarget.getBoundingClientRect();
    if (!r.width) return;
    const i = n === 1 ? 0 : Math.round(((e.clientX - r.left) / r.width) * (n - 1));
    setHoveredIdx(Math.max(0, Math.min(n - 1, i)));
  }

  return (
    <div className="fin-chart">
      <div className="fin-cardhead">
        <h2 className="fin-h2">{title}</h2>
        <ChipGroup
        multi
        label="Series shown"
        value={shown}
        onChange={setShown}
        options={SERIES.map(s => ({
          value: s.key,
          label: s.label,
          color: `pastel-${s.key === "invoiced" ? "periwinkle" : s.key === "received" ? "mint" : "pink"}`,
          icon: <span className={`fin-swatch fin-swatch--${s.key}`} />,
        }))}
        />
      </div>

      <div className="fin-chart__frame">
        <div className="fin-chart__y" aria-hidden="true">
          {ticks.map((t, i) => (
            <span key={i} style={{ top: `${yPct(t)}%` }}>{fmtMoneyShort(t)}</span>
          ))}
        </div>

        <div className="fin-chart__plotwrap">
          <div
            className="fin-chart__plot"
            onMouseMove={pick}
            onMouseLeave={() => setHoveredIdx(null)}
            onClick={pick}
          >
            <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img"
              aria-label={`Revenue trend: ${visible.map(s => s.label).join(", ") || "no series shown"}`}>
              {ticks.map((t, i) => (
                <line key={i} x1="0" x2={W} y1={yOf(t)} y2={yOf(t)}
                  className={t === 0 ? "fin-chart__base" : "fin-chart__grid"} vectorEffect="non-scaling-stroke" />
              ))}
              {visible.map(s => (
                <g key={s.key} data-series={s.key}>
                  {s.key === "received" && (
                    <polyline points={points(s.key)} className="fin-line fin-line--casing" vectorEffect="non-scaling-stroke" />
                  )}
                  <polyline points={points(s.key)} className={`fin-line fin-line--${s.key}`} vectorEffect="non-scaling-stroke" />
                </g>
              ))}
            </svg>

            {tip && (
              <>
                <span className="fin-chart__rule" style={{ left: `${xPct(hoveredIdx)}%` }} aria-hidden="true" />
                {visible.map(s => (
                  <span key={s.key} className={`fin-chart__dot fin-swatch--${s.key}`} aria-hidden="true"
                    style={{ left: `${xPct(hoveredIdx)}%`, top: `${yPct(tip[s.key] || 0)}%` }} />
                ))}
                <div className="fin-chart__tip" role="status"
                  style={xPct(hoveredIdx) > 55 ? { right: `${100 - xPct(hoveredIdx)}%` } : { left: `${xPct(hoveredIdx)}%` }}>
                  <strong>{tip.label}</strong>
                  {SERIES.map(s => (
                    <span key={s.key}><span className={`fin-swatch fin-swatch--${s.key}`} aria-hidden="true" />{s.label}: {fmtMoney(tip[s.key] || 0)}</span>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="fin-chart__x" aria-hidden="true">
            {data.map((d, i) => showLabel(i) && (
              <span key={i} className={i === 0 ? "is-first" : i === n - 1 ? "is-last" : undefined}
                style={i === 0 || i === n - 1 ? undefined : { left: `${xPct(i)}%` }}>
                {d.label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default memo(RevenueChart);
